import { Typography } from '@mui/material';
import { currencyFormatter } from 'src/utils/currency-formatter';
import { useTheme, alpha } from '@mui/material/styles';
import { forecastSegments, nanGuard } from './utils';

export default function cashflowForecast(period, income, expenses, profile) {
  const { fullRangeIncomePostTax, fullRangeIncome } = income ?? {};
  const { segments, fullRangeSpending } = expenses ?? {};
  const { splice } = expenses.datasets;

  // Destructure datasets
  const { historic: ic_historic, forecast: ic_forecast, budgeted: ic_budget } = income.datasets;
  const { historic: ex_historic, forecast: ex_forecast, budgeted: ex_budget } = expenses.datasets;

  // Centralized splicing logic
  const spliceData = (historic, forecast, budget) => ({
    historic: splice(historic, {}, segments, 0, true),
    budget: splice(forecast, budget, segments, 0, true),
  });

  const earnings = spliceData(ic_historic, ic_forecast, ic_budget);
  const spending = spliceData(ex_historic, ex_forecast, ex_budget);

  const incomeCategories = Object.keys(earnings.budget);
  const spendingCategories = Object.keys(spending.budget);

  // Helper function to calculate actual values
  const calculateValuesForCategory = (categoryData) => {
    const { data, postTax } = categoryData ?? {};
    return Object.fromEntries(Object.entries(postTax ?? data ?? {}).map(([k, v]) => [k, -v])) ?? [];
  };

  const actualSpendingForCategory = (cat) => calculateValuesForCategory(spending.historic?.[cat]);
  const actualIncomeForCategory = (cat) =>
    earnings.historic?.[cat]?.postTax ?? earnings.historic?.[cat]?.data ?? {};
  const budgetedSpendingForCategory = (cat) => calculateValuesForCategory(spending.budget?.[cat]);
  const budgetedIncomeForCategory = (cat) =>
    earnings.budget?.[cat]?.postTax ?? earnings.budget?.[cat]?.data ?? {};

  const spendingData = spendingCategories.map((category) => ({
    id: category,
    budget: budgetedSpendingForCategory(category),
    actual: actualSpendingForCategory(category),
  }));

  const earningsData = incomeCategories.map((category) => ({
    id: category,
    budget: budgetedIncomeForCategory(category),
    actual: actualIncomeForCategory(category),
  }));

  // Helper function to sum values
  const sumValues = (data) =>
    data.reduce((accumulator, obj) => {
      Object.entries(obj).forEach(([label, value]) => {
        accumulator[label] = (accumulator[label] || 0) + nanGuard(value);
      });
      return accumulator;
    }, {});

  const actualExpenses = sumValues(spendingData.map((obj) => obj.actual));
  const budgetExpenses = sumValues(spendingData.map((obj) => obj.budget));
  const actualIncome = sumValues(earningsData.map((obj) => obj.actual));
  const budgetIncome = sumValues(earningsData.map((obj) => obj.budget));

  const budgetedCashflow = {};
  const actualCashflow = {};
  const budgetedPostTaxCashflow = {};
  const actualPostTaxCashflow = {};

  segments?.forEach(({ label }) => {
    const actual_income = actualIncome[label] || 0;
    const budget_income = budgetIncome[label] || 0;
    const actual_expenses = actualExpenses[label] || 0;
    const budget_expenses = budgetExpenses[label] || 0;

    // Pre-tax cashflow
    budgetedCashflow[label] = nanGuard(budget_income) + nanGuard(budget_expenses);
    actualCashflow[label] = nanGuard(actual_income) + nanGuard(actual_expenses);

    // Post-tax cashflow using post-tax income values
    const actual_post_tax_income =
      fullRangeIncomePostTax[segments.findIndex((s) => s.label === label)] || 0;
    const budget_post_tax_income =
      fullRangeIncomePostTax[segments.findIndex((s) => s.label === label)] || 0;

    budgetedPostTaxCashflow[label] = nanGuard(budget_post_tax_income) + nanGuard(budget_expenses);
    actualPostTaxCashflow[label] = nanGuard(actual_post_tax_income) + nanGuard(actual_expenses);
  });

  // Compute functions using segment labels
  const computePreTaxIncome = forecastSegments(segments, (_, { index }) => fullRangeIncome[index]);

  const computePostTaxIncome = forecastSegments(
    segments,
    (_, { index }) => fullRangeIncomePostTax[index]
  );

  const computeExpenses = forecastSegments(segments, (label, { index, future }) =>
    future ? budgetExpenses[label] : actualExpenses[label]
  );

  // const computeCashflow = forecastSegments(segments, (label, { index, future }) =>
  //   future ? budgetedCashflow[label] : actualCashflow[label]
  // );

  const computePostTaxCashflow = forecastSegments(segments, (label, { index, future }) =>
    future ? budgetedPostTaxCashflow[label] : actualPostTaxCashflow[label]
  );

  // Generate cumulative cash flows as key-value pairs
  const cumulativeCashFlows = {};
  let cumulativeTotal = 0;

  // eslint-disable-next-line no-restricted-syntax
  for (const [month, cashFlow] of Object.entries(computePostTaxCashflow)) {
    cumulativeTotal += cashFlow;
    cumulativeCashFlows[month] = cumulativeTotal;
  }

  return {
    rows: [
      { id: 1, key: 'beforeTaxIncome', title: 'Before Tax Income', data: computePreTaxIncome },
      { id: 1, key: 'afterTaxIncome', title: 'After Tax Income', data: computePostTaxIncome },
      { id: 2, key: 'expenses', title: 'Total Expenses', data: computeExpenses },
      // { id: 3, key: 'preTaxNetCashFlow', title: 'Net Cashflow', data: computeCashflow },
      { id: 3, key: 'netCashFlow', title: 'Net Cashflow', data: computePostTaxCashflow },
      { id: 4, key: 'cumNetCashFlow', title: 'Cumulative Net Cashflow', data: cumulativeCashFlows },
    ],
    columns: segments
      .map((segment, index) => ({
        ...segment,
        field: segment.label,
        headerName: segment.label,
        type: 'number',
        headerClassName: index % 2 === 0 ? 'even' : 'odd',
        valueGetter: (_, row) => row?.data?.[segment.label] ?? 0,
        renderCell: (params) => renderCell({ period, budgets: [], segment, params }),
        valueFormatter: (value) => currencyFormatter.format(value),
      }))
      .concat({
        field: 'title',
        headerName: 'Category',
        width: 200,
      }),
    segments,
    budgetedCashflow,
    actualCashflow,
    fullRangeIncome: fullRangeIncomePostTax,
    fullRangeSpending,
  };
}

const renderCell = ({ segment, params }) => {
  const theme = useTheme();
  const colorForValue = (num, future = false) =>
    num < 0
      ? alpha(theme.palette.error.main, future ? 0.6 : 1)
      : future
      ? 'text.disabled'
      : 'inherit';

  return (
    <Typography color={colorForValue(params.value, segment.future)}>
      {params?.formattedValue}
    </Typography>
  );
};
